import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, changePasswordSchema } from "@utils/index";
import { ChangePasswordFormInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { useUserChangePassword } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  MobileHeader,
  Form,
  FormRow,
  FormInput,
  Button,
} from "@components/index";

export const ChangePasswordForm = () => {
  const setState = useCommonStore(({ setState }) => setState);
  const notify = useNotify();
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });

  const { mutate, isPending: isPendingChangePassword } = useUserChangePassword(
    () => {
      reset();
      notify("success", "Your password has been successfully updated.");
    },
    ({ message, fields }) => {
      Object.keys(fields || {}).forEach((key) =>
        setError(key as "currentPassword" | "password" | "confirmPassword", {
          message: fields![key],
        }),
      );
      notify("error", message);
    },
  );

  useEffect(() => {
    setState({ isLoading: isPendingChangePassword });
  }, [isPendingChangePassword, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
  } = useForm<ChangePasswordFormInterface>({
    mode: "onBlur",
    disabled: isPendingChangePassword,
    resolver: yupResolver(changePasswordSchema),
  });

  const onSubmit: SubmitHandler<ChangePasswordFormInterface> = (data) =>
    mutate({ ...data });

  return (
    <>
      <MobileHeader href="/profile" title="Change Password" />
      <div className="px-4 pt-10 text-center text-black-54 md:max-w-[632px] md:px-24 md:pt-2 md:text-left">
        Choose a strong password and don't reuse it for other accounts. Changing
        your password will sign you out on your devices.
      </div>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 text-black-100 md:max-w-[632px] md:px-24"
      >
        <FormRow top={isMobile ? "medium" : "large"}>
          <FormInput
            {...register<"currentPassword">("currentPassword")}
            name="currentPassword"
            type="password"
            label="Enter your password"
            error={formState.errors.currentPassword?.message}
            onChange={({ currentTarget }) => {
              setValue("currentPassword", currentTarget.value.trim());
              clearErrors("currentPassword");
            }}
          />
        </FormRow>
        <FormRow>
          <FormInput
            {...register<"password">("password")}
            name="password"
            type="password"
            label="New Password"
            helpText="Your password should include minimum 8 characters, at least 1 uppercase letter, 1 lowercase letter and 1 number"
            error={formState.errors.password?.message}
            onChange={({ currentTarget }) => {
              setValue("password", currentTarget.value.trim());
              clearErrors("password");
            }}
          />
        </FormRow>
        <FormRow>
          <FormInput
            {...register<"confirmPassword">("confirmPassword")}
            name="confirmPassword"
            type="password"
            label="Confirm Password"
            error={formState.errors.confirmPassword?.message}
            onChange={({ currentTarget }) => {
              setValue("confirmPassword", currentTarget.value.trim());
              clearErrors("confirmPassword");
            }}
          />
        </FormRow>
        <FormRow isRow isBetween top="medium">
          <Button type="submit" title="Change password" className="w-52" />
          <Button
            title="Cancel"
            variant="text"
            isLink
            href="/profile"
            className="text-black-54"
          />
        </FormRow>
      </Form>
    </>
  );
};
